import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Typography, Paper, Alert, CircularProgress, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "../../../config/axiosConfig";
import ENV from "../../../config/env";
import { PROYECTO_COLOR } from "../theme";

const formatTamano = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// Reemplaza el bloque de archivos de visualizar.php (scandir() crudo sobre
// uploads/{cod}/, sin BD) por el mismo patrón ya establecido en el módulo Clientes
// (ContratosServicioDialog.jsx): archivos trackeados en BD (mod_proyecto_archivo),
// con nombre original, tamaño y quién subió.
const ArchivosProyectoSection = ({ proyectoId }) => {
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(`/api/proyectos/${proyectoId}/archivos`)
      .then((res) => setArchivos(res.data?.data || []))
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar los archivos."))
      .finally(() => setLoading(false));
  }, [proyectoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSeleccionar = () => fileInputRef.current?.click();

  const handleArchivosElegidos = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append("archivos", file));
      await axios.post(`/api/proyectos/${proyectoId}/archivos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      cargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron subir los archivos.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleEliminar = async (archivo) => {
    if (!window.confirm(`¿Eliminar "${archivo.nombre_original}"?`)) return;
    setDeletingId(archivo.id);
    try {
      await axios.delete(`/api/proyectos/${proyectoId}/archivos/${archivo.id}`);
      cargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo eliminar el archivo.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: PROYECTO_COLOR }}>
        Archivos
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <input ref={fileInputRef} type="file" multiple hidden onChange={handleArchivosElegidos} />
      <Box
        sx={{
          border: "2px dashed", borderColor: "divider", borderRadius: 1, p: 2,
          textAlign: "center", cursor: uploading ? "default" : "pointer", mb: 1.5,
        }}
        onClick={uploading ? undefined : handleSeleccionar}
      >
        {uploading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 28, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ mt: 0.5 }}>Click para elegir uno o varios archivos</Typography>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}><CircularProgress size={20} /></Box>
      ) : archivos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Sin archivos subidos.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
            <TableHead>
              <TableRow>
                <TableCell>Archivo</TableCell>
                <TableCell>Tamaño</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {archivos.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <InsertDriveFileIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                      <a href={`${ENV.API_BASE_URL}${a.archivo_url}`} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
                        {a.nombre_original}
                      </a>
                    </Box>
                  </TableCell>
                  <TableCell>{formatTamano(a.tamano)}</TableCell>
                  <TableCell>{a.usuario || "-"}</TableCell>
                  <TableCell>{formatFecha(a.fecha)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Eliminar">
                      <span>
                        <IconButton size="small" color="error" onClick={() => handleEliminar(a)} disabled={deletingId === a.id}>
                          {deletingId === a.id ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 15 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ArchivosProyectoSection;
